import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import type { OasisApiResponse, OasisPublicConfig } from 'shared-resources';

import { environment } from '../../../environments/environment';
import { getCurrentCycleYear } from './current-cycle';

/**
 * The cycle configuration this app runs on, fetched once at bootstrap.
 *
 * Replaces three things the front end used to decide for itself, two of which were
 * live defects rather than untidiness:
 *
 *   - `portalOpensAt`, hand-maintained across five environment files.
 *   - The cycle year, guessed from the calendar month.
 *   - The J.D. recency cutoff. The client derived October 1 of (cycleYear − 4) while
 *     the server honours a per-cycle override — so the first time OARM sets one, the
 *     client's hard stop and the server's submit-time backstop disagree, either
 *     blocking an eligible applicant silently or letting one through to be rejected
 *     at the final step.
 *
 * ## Never rejects
 *
 * `load()` is an `APP_INITIALIZER`. An initializer that throws white-screens the
 * whole app, so every failure path here is caught and logged. `OasisAuthService.init`
 * already does the same for its discovery document.
 *
 * ## Fallbacks fail to TODAY'S behaviour, not to "closed"
 *
 * When config is unavailable the accessors return undefined and each consumer keeps
 * its own documented fallback — `isPortalOpen` treats a missing timestamp as OPEN,
 * the cycle year falls back to the local derivation. That is deliberate: failing
 * closed would take a live portal down over one transient 500, while failing open
 * costs at most a confusing login attempt, because the server is the real gate
 * (`evaluateSaveAuthorization` → `WINDOW_CLOSED`) and never trusts these values.
 *
 * ## Reading it synchronously is safe
 *
 * Angular resolves every `APP_INITIALIZER` before the first component renders, so
 * synchronous callers after bootstrap always see loaded config. No consumer needs to
 * become async.
 */
@Injectable({ providedIn: 'root' })
export class OasisConfigService {
	private readonly http = inject(HttpClient);
	private readonly base = environment.oasisApiBase.replace(/\/$/, '');

	private readonly _config = signal<OasisPublicConfig | null>(null);

	/** The loaded config, or null when it could not be read. */
	readonly config = this._config.asReadonly();

	/**
	 * Fetch the config. Resolves even on failure — see the note above.
	 *
	 * Runs before `OasisAuthService.init()` has necessarily finished, which is fine:
	 * the route is unauthenticated, and `authInterceptor` skips a request when there
	 * is no token rather than sending an empty bearer.
	 */
	async load(): Promise<void> {
		try {
			const response = await firstValueFrom(this.http.get<OasisApiResponse<OasisPublicConfig>>(`${this.base}/oasis-config`));
			if (response.success && response.payload) {
				this._config.set(response.payload);
				return;
			}
			// A `success: false` envelope is the expected shape for an unconfigured
			// cycle (`CYCLE_NOT_CONFIGURED`), which is an ops problem, not a client bug.
			console.error('[OasisConfig] config unavailable', response.error?.code, response.error?.message);
		} catch (err) {
			console.error('[OasisConfig] config request failed', err);
		}
	}

	/** When applications open. Undefined when config is unavailable. */
	submissionOpens(): string | undefined {
		return this._config()?.submissionOpens;
	}

	/** When applications close — and, at the same instant, component review opens. */
	submissionDeadline(): string | undefined {
		return this._config()?.submissionDeadline;
	}

	/** End of the HP→SLIP-Law-Clerk window. Undefined when the cycle has none. */
	programChangeDeadline(): string | undefined {
		return this._config()?.programChangeDeadline;
	}

	/**
	 * The cycle year, falling back to the local month-based derivation.
	 *
	 * The fallback is the app's previous behaviour, so losing config degrades to the
	 * status quo rather than breaking navigation — an applicant with no cycle year has
	 * no dashboard at all.
	 */
	cycleYear(): number {
		return this._config()?.cycleYear ?? getCurrentCycleYear();
	}

	/** Applicant-facing cycle label, e.g. "OASIS 2027 Application Cycle". */
	label(): string | undefined {
		return this._config()?.label;
	}

	/** OMB control number and expiration, for the Paperwork Reduction Act notice. */
	ombNumber(): string | undefined {
		return this._config()?.ombNumber;
	}

	ombExpirationDate(): string | undefined {
		return this._config()?.ombExpirationDate;
	}

	/**
	 * The RESOLVED J.D. recency cutoff — the per-cycle override when OARM set one,
	 * otherwise the standing October 1 rule, decided server-side.
	 *
	 * Undefined only when config is unavailable, in which case the eligibility rules
	 * fall back to deriving the standing rule locally. That fallback is the exact
	 * divergence this exists to remove, so it is a last resort, not a design.
	 */
	jdEligibilityCutoff(): string | undefined {
		return this._config()?.jdEligibilityCutoff;
	}
}
